import type {
  BranchChangesets,
  ConciseBranch,
} from '@festina/configuration-general-types/configuration'
import {
  type BranchKey,
  LayerKey,
  useImportBranchChangesets,
  useInvalidateAllConciseBranches,
  useSquashBranch,
  type WorkspaceKey,
} from '@festina-life/api'
import {
  assertNotEmpty,
  type ConfigurableServer,
  notEmpty,
  type Optional,
} from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { useCallback, useMemo, useState } from 'react'

import { useMetaModelContext } from '../../../../context/metaModelContext'
import { isProtectedBranchCategory } from '../../../../uiModels/branches/types'
import { useInvalidateBranchAfterHistoryRewrite } from '../../../../uiModels/branches/useInvalidateBranchAfterHistoryRewrite'
import { useConfigurationComponentWorkspaces } from '../../../../uiModels/workspace/useConfigurationComponentWorkspaces'
import { useWorkspaceManagerContext } from '../../../../workspace/workspaceManagerContext'
import { useCreateBranch } from '../../../branchCreator/useCreateBranch'
import type { MergeBranchInput } from './MergeBranchForm'
import type { MergeResult, MergeStatus } from './types'

export const useMergeBranch = () => {
  const translate = useTranslate()
  const [status, setStatus] = useState<MergeStatus>()
  const [error, setError] = useState('')
  const { mutateAsync: triggerCreateBranch } = useCreateBranch()
  const { mutateAsync: triggerImportChangesets } = useImportBranchChangesets()
  const invalidateBranches = useInvalidateAllConciseBranches()
  const { mutateAsync: triggerSquash } = useSquashBranch()
  const { onCreate } = useWorkspaceManagerContext()
  const { addOrUpdateOperation } = useConfigurationComponentWorkspaces()
  const { blockTopology } = useMetaModelContext()
  const invalidateBranchAfterHistoryRewrite =
    useInvalidateBranchAfterHistoryRewrite()
  const rootBlock = blockTopology.name

  const reportFailure = useCallback((reason: string) => {
    setStatus('error')
    setError(reason)
  }, [])

  const mutateAsync = useCallback(
    async ({
      branchChangesets,
      workspaceChangesets,
      mergeBranchName,
      shouldSquash,
      sourceBranch,
      targetBranch,
      server,
      mergeConflictsWorkspaceName,
    }: MergeBranchInput & {
      mergeConflictsWorkspaceName: string
      branchChangesets: BranchChangesets
      workspaceChangesets?: Optional<BranchChangesets>
      targetBranch: ConciseBranch
      sourceBranch: ConciseBranch
      server: ConfigurableServer
    }): Promise<Optional<MergeResult>> => {
      setError('')

      const targetBranchName = targetBranch.name
      const sourceBranchName = sourceBranch.name

      assertNotEmpty(sourceBranchName)
      assertNotEmpty(targetBranchName)

      const isProtectedTarget = isProtectedBranchCategory(
        targetBranch.branchCategory,
      )
      // Protected targets cannot receive changes directly, so the merge lands on
      // a child feature branch; non-protected targets receive the changes directly.
      const hostBranchName = isProtectedTarget
        ? mergeBranchName
        : targetBranchName
      const hostBranchKey: BranchKey = { name: hostBranchName, server }

      try {
        if (isProtectedTarget) {
          setStatus('creatingBranch')

          await triggerCreateBranch({
            branchName: hostBranchName,
            baseBranch: targetBranch,
            server,
            category: 'feature',
            shouldDefineTypes: false,
            shouldAddData: false,
          })
        }

        setStatus('addingChangesets')

        await triggerImportChangesets({
          server,
          branchName: hostBranchName,
          configurationData: branchChangesets,
        })

        // Protected targets always squash so the merge lands as a single
        // changeset on the child branch; for non-protected targets squashing is
        // opt-in via the form.
        if (isProtectedTarget || shouldSquash) {
          setStatus('squashingBranch')

          await triggerSquash({
            server,
            branchName: hostBranchName,
            description: translate(
              'branchDetailsPanel.tabs.manage.merge.changesetDescription',
              {
                sourceBranchName,
                targetBranchName,
              },
            ),
          })
        }

        await invalidateBranches()
        await invalidateBranchAfterHistoryRewrite(hostBranchKey)

        if (workspaceChangesets) {
          setStatus('creatingWorkspace')

          await onCreate(
            {
              name: mergeConflictsWorkspaceName,
              branchKey: hostBranchKey,
            },
            true,
          )

          const conflictsWorkspaceKey: WorkspaceKey = {
            name: mergeConflictsWorkspaceName,
            branchKey: hostBranchKey,
          }

          const addOperations = (
            workspaceChangesets.branchChangesets?.filter(notEmpty) ?? []
          ).flatMap((branchChangeset) =>
            (branchChangeset.changeset?.operations?.filter(notEmpty) ?? []).map(
              (operation) =>
                addOrUpdateOperation({
                  operation,
                  layer: new LayerKey({
                    tenantId: branchChangeset.changeset?.tenantId,
                    layerId: branchChangeset.changeset?.layer,
                  }),
                  block: branchChangeset.block ?? rootBlock,
                  workspaceKey: conflictsWorkspaceKey,
                }),
            ),
          )

          await Promise.all(addOperations)

          setStatus('successWithConflicts')

          return { hostBranchKey, conflictsWorkspaceKey }
        } else {
          setStatus('successNoConflicts')

          return { hostBranchKey, conflictsWorkspaceKey: undefined }
        }
      } catch {
        reportFailure(
          translate('branchDetailsPanel.tabs.manage.merge.error.unknown'),
        )

        return undefined
      }
    },
    [
      addOrUpdateOperation,
      invalidateBranches,
      onCreate,
      reportFailure,
      translate,
      triggerCreateBranch,
      triggerImportChangesets,
      triggerSquash,
      rootBlock,
      invalidateBranchAfterHistoryRewrite,
    ],
  )

  return useMemo(
    () => ({ mutateAsync, status, error }),
    [error, status, mutateAsync],
  )
}
